import React from "react";

import { useNavigate } from "utils";

import { FarmVerification, PracticeVerificationCounts } from "models";

import { Col, Icon, IconButton, Row, TablePanel, Tag, Typography } from "components";

type PracticeState = {
  text: string;
  icon: string;
  color: string;
};

const getPracticeState = (counts: { verified: number; rejected: number; pending: number } | undefined): PracticeState => {
  if (!counts) {
    return { text: "no data", icon: "tabler/help", color: "grey-600" };
  }

  const { verified, rejected, pending } = counts;
  const total = verified + rejected + pending;

  if (total === 0) {
    return { text: "no data", icon: "tabler/help", color: "grey-600" };
  }

  if (pending > 0) {
    return { text: "not verified", icon: "tabler/help", color: "grey-600" };
  }

  if (rejected > 0 && verified > 0) {
    return { text: "partially approved", icon: "tabler/circle-check", color: "green" };
  }

  if (rejected > 0 && verified === 0) {
    return { text: "rejected", icon: "tabler/circle-x", color: "grey" };
  }

  return { text: "approved", icon: "tabler/circle-check", color: "green" };
};

const PRACTICE_CONFIG: Array<{
  slug: keyof PracticeVerificationCounts;
  headline: string;
  icon: string;
  tabKey: string;
}> = [
  { slug: "crops", headline: "Crops", icon: "klim/plant", tabKey: "crops" },
  { slug: "mineralFertilizers", headline: "Mineral Fertilizers", icon: "klim/fertilizer", tabKey: "fertilizers" },
  { slug: "organicFertilizers", headline: "Organic Fertilizers", icon: "klim/fertilizer", tabKey: "fertilizers" },
  { slug: "tillages", headline: "Tillages", icon: "klim/tillage", tabKey: "tillage-and-seeding" },
  { slug: "seedings", headline: "Seedings", icon: "klim/seeding", tabKey: "tillage-and-seeding" },
];

export const TableSubPanels = ({ farm }: { farm: FarmVerification }) => {
  const { verified, fieldVerificationsCounts, practiceVerificationsCounts } = farm;

  const navigate = useNavigate();
  const farmState = {
    color: "green",
    text: "approved",
    icon: "tabler/circle-check",
  };
  if (farm.verified === null) {
    farmState.color = "grey-600";
    farmState.text = "not verified";
    farmState.icon = "tabler/help";
  } else if (farm.verified === false) {
    farmState.color = "grey";
    farmState.text = "rejected";
    farmState.icon = "tabler/circle-x";
  }

  const { verifiedFieldCount, rejectedFieldCount, unverifiedFieldCount } = fieldVerificationsCounts;
  const fieldVerificationsCount = verifiedFieldCount + rejectedFieldCount + unverifiedFieldCount;

  const fieldsState = {
    text: "approved",
    icon: "tabler/circle-check",
    color: "green",
  };

  if (unverifiedFieldCount === 0 && rejectedFieldCount > 0 && verifiedFieldCount > 0) {
    fieldsState.text = "partially approved";
    fieldsState.icon = "tabler/circle-check";
  } else if (rejectedFieldCount > 0 && rejectedFieldCount === fieldVerificationsCount) {
    fieldsState.text = "rejected";
    fieldsState.icon = "tabler/circle-x";
    fieldsState.color = "grey";
  } else if (unverifiedFieldCount > 0) {
    fieldsState.text = "not verified";
    fieldsState.icon = "tabler/help";
    fieldsState.color = "grey-600";
  }

  return (
    <div key={farm.id}>
      <Row gutter={[0, 0]} className="farms-sub-panel">
        <Col sm={15} md={15}>
          {/* @ts-expect-error TablePanel not yet in TS */}
          <TablePanel headline="Farm" icon="klim/farm" tag={verified === null && <Tag colour="yellow" text="pending verification" />} />
        </Col>
        <Col sm={2} md={2}>
          {verified === null ? "0" : "1"}/1
        </Col>
        <Col sm={4} md={4} className={`${farmState.color} padding-left-100`}>
          <Icon iconName={farmState.icon} className="icon inline margin-right-25" />
          <Typography variant="paragraph-m">{farmState.text}</Typography>
        </Col>
        <Col sm={3} md={3} className="icon-container">
          <IconButton iconName="tabler/eye" type="secondary" onClick={() => navigate(`/verifications/farms/${farm.id}/farm`)} />
        </Col>
      </Row>
      <Row gutter={[0, 0]} className="farms-sub-panel">
        <Col sm={15} md={15}>
          {/* @ts-expect-error TablePanel not yet in TS */}
          <TablePanel headline="Fields" icon="klim/field" tag={unverifiedFieldCount > 0 && <Tag colour="yellow" text="pending verification" />} />
        </Col>
        <Col sm={2} md={2}>
          {rejectedFieldCount + verifiedFieldCount}/{fieldVerificationsCount}
        </Col>
        <Col sm={4} md={4} className={`${fieldsState.color} padding-left-100`}>
          <Icon iconName={fieldsState.icon} className="icon inline margin-right-25" />
          <Typography variant="paragraph-m">{fieldsState.text}</Typography>
        </Col>
        <Col sm={3} md={3} className="icon-container">
          <IconButton iconName="tabler/eye" type="secondary" onClick={() => navigate(`/verifications/farms/${farm.id}/fields`)} />
        </Col>
      </Row>
      {PRACTICE_CONFIG.map(({ slug, headline, icon, tabKey }) => {
        const counts = practiceVerificationsCounts?.[slug];
        const total = counts ? counts.verified + counts.rejected + counts.pending : 0;
        const verified = counts?.verified ?? 0;
        const rejected = counts?.rejected ?? 0;
        const practiceState = getPracticeState(counts);
        const hasPending = (counts?.pending ?? 0) > 0;

        // Skip practices with no data
        if (total === 0) {
          return null;
        }

        return (
          <Row key={slug} gutter={[0, 0]} className="farms-sub-panel">
            <Col sm={15} md={15}>
              {/* @ts-expect-error TablePanel not yet in TS */}
              <TablePanel headline={headline} icon={icon} tag={hasPending && <Tag colour="yellow" text="pending verification" />} />
            </Col>
            <Col sm={2} md={2}>
              {verified + rejected}/{total}
            </Col>
            <Col sm={4} md={4} className={`${practiceState.color} padding-left-100`}>
              <Icon iconName={practiceState.icon} className="icon inline margin-right-25" />
              <Typography variant="paragraph-m">{practiceState.text}</Typography>
            </Col>
            <Col sm={3} md={3} className="icon-container">
              <IconButton iconName="tabler/eye" type="secondary" onClick={() => navigate(`/verifications/farms/${farm.id}/${tabKey}`)} />
            </Col>
          </Row>
        );
      })}
    </div>
  );
};
